import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { User } from "@/services/auth";
import { authService } from "@/services/auth";
import { sessionManager, type SessionInfo } from "@/lib/session-manager";

// BYK-23: Session refresh handler for 401 errors
let sessionRefreshListener: (() => void) | null = null;
let sessionManagerInitialized = false;

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isNetworkError: boolean;
  networkErrorMessage: string | null;

  // Actions
  initialize: () => Promise<void>;
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setNetworkError: (hasError: boolean, message?: string) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true,
      isNetworkError: false,
      networkErrorMessage: null,

      // BYK-170: Initialize auth state from session with proactive refresh
      initialize: async () => {
        try {
          console.log("[Auth] Initializing session...");

          // Set up session refresh listener for 401 errors
          if (typeof window !== "undefined" && !sessionRefreshListener) {
            sessionRefreshListener = () => {
              console.log("[Auth] Session expired event received, attempting silent refresh...");
              authService.getSession().then((session) => {
                if (session?.user) {
                  console.log("[Auth] Silent session refresh successful");
                  set({
                    user: session.user,
                    isAuthenticated: true,
                  });
                  // BYK-171: Update session manager with new expiry
                  if (session.session?.expiresAt) {
                    sessionManager.startMonitoring({
                      expiresAt: session.session.expiresAt,
                      userId: session.user.id,
                    });
                  }
                } else {
                  console.log("[Auth] Silent refresh failed, session invalid");
                  set({
                    user: null,
                    isAuthenticated: false,
                  });
                }
              }).catch((err) => {
                console.error("[Auth] Silent refresh error:", err);
              });
            };
            window.addEventListener("auth:session-expired", sessionRefreshListener);
            console.log("[Auth] Session refresh listener attached");
          }

          // BYK-173: Set up network status listener
          if (typeof window !== "undefined") {
            window.addEventListener("network:status", ((event: CustomEvent) => {
              const { offline, message } = event.detail;
              set({
                isNetworkError: offline,
                networkErrorMessage: message || null,
              });
            }) as EventListener);
          }

          // BYK-171: Initialize session manager for proactive refresh
          if (!sessionManagerInitialized) {
            sessionManager.init({
              onSessionRefresh: async (): Promise<SessionInfo | null> => {
                const session = await authService.getSession();
                if (session?.user && session.session) {
                  set({
                    user: session.user,
                    isAuthenticated: true,
                  });
                  return {
                    expiresAt: session.session.expiresAt,
                    userId: session.user.id,
                  };
                }
                return null;
              },
              onSessionExpired: () => {
                console.log("[Auth] Session manager reports session expired");
                set({
                  user: null,
                  isAuthenticated: false,
                });
              },
              onNetworkError: (message: string) => {
                console.log("[Auth] Network error:", message);
                set({
                  isNetworkError: true,
                  networkErrorMessage: message,
                });
              },
            });
            sessionManagerInitialized = true;
          }

          const session = await authService.getSession();
          if (session?.user) {
            console.log("[Auth] Session valid for:", session.user.email);
            set({
              user: session.user,
              isAuthenticated: true,
              isLoading: false,
            });

            // BYK-171: Start proactive refresh monitoring
            if (session.session?.expiresAt) {
              sessionManager.startMonitoring({
                expiresAt: session.session.expiresAt,
                userId: session.user.id,
              });
            }
          } else {
            console.log("[Auth] No valid session found");
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
            });
          }
        } catch (error) {
          console.error("[Auth] Initialization failed:", error);
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      setUser: (user) =>
        set({
          user,
          isAuthenticated: !!user,
          isLoading: false,
        }),

      setLoading: (isLoading) => set({ isLoading }),

      setNetworkError: (hasError, message) =>
        set({
          isNetworkError: hasError,
          networkErrorMessage: message || null,
        }),

      logout: () => {
        sessionManager.destroy();
        sessionManagerInitialized = false;
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          isNetworkError: false,
          networkErrorMessage: null,
        });
      },
    }),
    {
      name: "byk-auth",
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

export default useAuthStore;
